import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ValidationRuleConfig, ValidationRuleUpdate } from '@complan/contracts';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TextInput } from '@/features/initiative/intake/fields';
import { FindingsAlert } from './FindingsAlert';
import { useUpdateValidationRules, useValidationRules } from './admin-data';
import { findingsFromError } from './admin-findings';

/**
 * The validation-rule configuration table (task 23.6, R8.9).
 *
 * The API returns every registered rule with a `configurable` flag. R8.9 withholds
 * the hard rules of R8.3 and the information context of R8.7 from configuration,
 * and the endpoint reports them with `configurable: false`. This table renders
 * those as fixed — their tier, message and state are shown for reference but no
 * control edits them. Only the configurable rules expose an editable tier (the
 * three permitted tiers, never `hard`), an override message and an active toggle.
 * The save submits only the configurable rules; the server is the backstop that
 * rejects a change to a non-configurable rule.
 */

/** The editable tiers R8.9 permits — never `hard`, which would create a blocking rule. */
const EDITABLE_TIERS: readonly ValidationRuleUpdate['tier'][] = ['governance', 'warning', 'information'];

/** The per-rule draft the editable rows track. */
interface RuleDraft {
  tier: ValidationRuleUpdate['tier'];
  message: string;
  isActive: boolean;
}

export function ValidationRulesSection(): React.JSX.Element {
  const { t } = useTranslation();
  const rulesQuery = useValidationRules();
  const updateMutation = useUpdateValidationRules();
  const rules = useMemo(() => rulesQuery.data?.rules ?? [], [rulesQuery.data]);

  const [drafts, setDrafts] = useState<Record<string, RuleDraft>>({});

  // Seed drafts from the fetched configurable rules once they load or change.
  const seedKey = rules.map((rule) => `${rule.ruleId}:${rule.tier}:${String(rule.isActive)}`).join('|');
  const [lastSeed, setLastSeed] = useState('');
  if (seedKey !== lastSeed) {
    setLastSeed(seedKey);
    const seeded: Record<string, RuleDraft> = {};
    for (const rule of rules) {
      if (rule.configurable) {
        seeded[rule.ruleId] = {
          tier: normaliseTier(rule.tier),
          message: rule.message,
          isActive: rule.isActive,
        };
      }
    }
    setDrafts(seeded);
  }

  const findings = findingsFromError(updateMutation.error);

  const save = (): void => {
    const updates: ValidationRuleUpdate[] = rules
      .filter((rule) => rule.configurable)
      .map((rule) => {
        const draft = drafts[rule.ruleId];
        return {
          ruleId: rule.ruleId,
          tier: draft?.tier ?? normaliseTier(rule.tier),
          message: draft?.message ?? rule.message,
          isActive: draft?.isActive ?? rule.isActive,
        };
      });
    if (updates.length > 0) {
      updateMutation.mutate({ rules: updates });
    }
  };

  return (
    <Card className="gap-4 p-[14px]">
      <div>
        <p className="text-[13px] font-semibold text-txt">{t('admin.validationRules.heading')}</p>
        <p className="text-muted-foreground mt-1 text-[11px]">{t('admin.validationRules.hint')}</p>
      </div>

      <FindingsAlert findings={findings} />

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-[10.5px] uppercase">{t('admin.validationRules.table.colRule')}</TableHead>
            <TableHead className="text-[10.5px] uppercase">{t('admin.validationRules.table.colTier')}</TableHead>
            <TableHead className="text-[10.5px] uppercase">{t('admin.validationRules.table.colMessage')}</TableHead>
            <TableHead className="text-[10.5px] uppercase">{t('admin.validationRules.table.colState')}</TableHead>
            <TableHead className="text-[10.5px] uppercase">{t('admin.validationRules.table.colConfigurable')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rulesQuery.isError ? (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground py-6 text-center text-[12.3px]">
                {t('admin.common.loadError')}
              </TableCell>
            </TableRow>
          ) : rules.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground py-6 text-center text-[12.3px]">
                {rulesQuery.isLoading ? t('admin.common.loading') : t('admin.validationRules.table.empty')}
              </TableCell>
            </TableRow>
          ) : (
            rules.map((rule) =>
              rule.configurable ? (
                <ConfigurableRuleRow
                  key={rule.ruleId}
                  rule={rule}
                  draft={drafts[rule.ruleId]}
                  onChange={(draft) => setDrafts((current) => ({ ...current, [rule.ruleId]: draft }))}
                />
              ) : (
                <FixedRuleRow key={rule.ruleId} rule={rule} />
              ),
            )
          )}
        </TableBody>
      </Table>

      <div className="flex items-center justify-end gap-2">
        {updateMutation.isSuccess ? (
          <span className="text-[11.5px] text-[#14724A]">{t('admin.validationRules.saved')}</span>
        ) : null}
        <Button type="button" size="sm" onClick={save} disabled={updateMutation.isPending}>
          {updateMutation.isPending ? t('admin.validationRules.saving') : t('admin.validationRules.save')}
        </Button>
      </div>
    </Card>
  );
}

/** A non-configurable rule: shown for reference, no control edits it (R8.9). */
function FixedRuleRow({ rule }: { readonly rule: ValidationRuleConfig }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <TableRow>
      <TableCell className="font-mono text-[12px] text-txt">{rule.ruleId}</TableCell>
      <TableCell className="text-[12.3px]">{t(`admin.validationRules.tier.${rule.tier}`)}</TableCell>
      <TableCell className="text-muted-foreground text-[12px] whitespace-normal">
        {rule.message}
      </TableCell>
      <TableCell className="text-[12.3px]">
        {rule.isActive ? t('admin.common.active') : t('admin.common.inactive')}
      </TableCell>
      <TableCell className="text-[12px] text-muted-foreground">
        {t('admin.validationRules.table.fixed')}
      </TableCell>
    </TableRow>
  );
}

/** A configurable rule: editable tier (never hard), override message and active toggle (R8.9). */
function ConfigurableRuleRow({
  rule,
  draft,
  onChange,
}: {
  readonly rule: ValidationRuleConfig;
  readonly draft: RuleDraft | undefined;
  readonly onChange: (draft: RuleDraft) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const current: RuleDraft = draft ?? {
    tier: normaliseTier(rule.tier),
    message: rule.message,
    isActive: rule.isActive,
  };

  return (
    <TableRow>
      <TableCell className="font-mono text-[12px] text-txt">{rule.ruleId}</TableCell>
      <TableCell>
        <select
          aria-label={t('admin.validationRules.table.colTier')}
          className="border-line h-8 rounded-[var(--radius-control)] border bg-panel px-2 text-[12px] text-txt"
          value={current.tier}
          onChange={(event) => onChange({ ...current, tier: event.target.value as RuleDraft['tier'] })}
        >
          {EDITABLE_TIERS.map((tier) => (
            <option key={tier} value={tier}>
              {t(`admin.validationRules.tier.${tier}`)}
            </option>
          ))}
        </select>
      </TableCell>
      <TableCell>
        <TextInput
          aria-label={t('admin.validationRules.table.colMessage')}
          placeholder={t('admin.validationRules.messagePlaceholder')}
          className="w-full min-w-[220px]"
          value={current.message}
          maxLength={500}
          onChange={(event) => onChange({ ...current, message: event.target.value })}
        />
      </TableCell>
      <TableCell>
        <label className="flex items-center gap-2 text-[12px] text-txt">
          <input
            type="checkbox"
            checked={current.isActive}
            onChange={(event) => onChange({ ...current, isActive: event.target.checked })}
          />
          <span>{current.isActive ? t('admin.common.active') : t('admin.common.inactive')}</span>
        </label>
      </TableCell>
      <TableCell className="text-[12px] text-[var(--accent-hover)]">
        {t('admin.validationRules.table.editable')}
      </TableCell>
    </TableRow>
  );
}

/**
 * Narrows the read tier to the editable set. A configurable rule never carries the
 * `hard` tier (R8.9), but the read type includes it; if one ever did, default it
 * to the closest blocking editable tier so the select has a valid value.
 */
function normaliseTier(tier: ValidationRuleConfig['tier']): ValidationRuleUpdate['tier'] {
  return tier === 'hard' ? 'governance' : tier;
}
